import tooltip from '@/core/charts/components/tooltip-standard'
import formatter from '@/core/charts/helper/formatter'
import chartColors from '@/core/charts/helper/chartColors'

export default {
  showLegend: false,
  showSmallLegend: true,
  chartOpts: getOptions(),
  chartData: (widget, data) => getChartData(data),
  chartInfo: (widget, data) => getChartInfo(data, 0),

  components: {
    tooltip
  }
}

function getOptions() {
  let baseCustSet = {
    chartGroupValue: 'Program',
    multipleSeries: true,
    dynamicHeight: true,
    hasDrillDown: false,
    showTotal: true,
    showTotalValue: 'Budget Used',
    recalculateGridMax: true,
    tooltip: {
      tooltipType: 'standard',
      width: '220px'
    }
  }

  let baseOpts = {
    customSettings: baseCustSet,
    stackBars: true,
    horizontalBars: true,
    seriesBarDistance: 20,
    labelInterpolationFnc: function (value) { return value[1] },
    fullWidth: false,
    targetLine: {
      value: 0,
      class: 'ct-target-line'
    },
    axisY: {
      position: 'start',
      labelOffset: { x: 2, y: 2 },
      scaleMinSpace: 40,
      offset: 125,
      showLabel: true,
      labelInterpolationFnc: function (value) { return value }
    },

    chartPadding: { top: 5, right: 50, bottom: 5, left: 5 }
  }
  return baseOpts
}

function getChartInfo(dataIn, pos) {
  let result = []
  dataIn.forEach(elem => {
    let gdPos = result.findIndex(k => k[0] === elem[pos])
    if (gdPos === -1) result.push(elem[pos])
  })
  return result.map(k => k[0])
}

function getChartData(data) {
  const ProgramName = 0
  const RevisedBudget = 1
  const BudgetBalance = 2
  const TotalProgramPOAmount = 3
  const TotalProgramExpenseAmount = 4
  const POTotalLessVatGst = 5
  const ExpenseTotalLessVatGst = 6
  const IncludeExpenseInBudget = 7
  const ExcludeVATFromBudget = 8
  const CostPerAttendee = 9
  const BudgetPerAttendee = 10

  let config = {}
  if (data.length === 2) {
    config = data[1]
    data.pop()
  }

  let sortedData = data.sort((b, a) => {
    let av = a[RevisedBudget]
    let bv = b[RevisedBudget]
    if (av < bv) return 1
    if (av > bv) return -1
    return 0
  })
  let hasPerAttendeeCols = sortedData[0].length === 11
  let revisedBudget = sortedData.map(k => k[RevisedBudget])
  let fullBudget = []
  let posP = []
  let expP = []
  let labelData = []
  let pos = sortedData.map(k => k[ExcludeVATFromBudget] ? k[POTotalLessVatGst] : k[TotalProgramPOAmount])
  let expenses = sortedData.map(k => k[IncludeExpenseInBudget] ? (k[ExcludeVATFromBudget] ? k[ExpenseTotalLessVatGst] : k[TotalProgramExpenseAmount]) : 0)
  let hasExpenses = false
  let isOverbudget = false

  for (let i = 0, n = expenses.length; i < n; i++) {
    if (expenses[i] !== 0) {
      hasExpenses = true
    }
    expP.push(Math.floor(expenses[i] / revisedBudget[i] * 100) || 0)
  }

  for (let i = 0, n = pos.length; i < n; i++) {
    posP.push(Math.floor(pos[i] / revisedBudget[i] * 100))
  }

  for (let i = 0, n = revisedBudget.length; i < n; i++) {
    fullBudget.push(100)
    let overBudget = (posP[i] + (expP[i] || 0)) > 100
    let label = (overBudget ? '!$red$! ' : '') + (posP[i] + (expP[i] || 0) + '% of ' + formatter.thousandsFormatter(revisedBudget[i]))
    if (posP[i] + (expP[i] || 0) < 0) {
      posP[i] = 0
      expP[i] = 0
    }
    if (overBudget) {
      let total = posP[i] + (expP[i] || 0)
      posP[i] = posP[i] / total * 100
      expP[i] = (expP[i] || 0) / total * 100
    }
    isOverbudget = overBudget

    labelData.push(revisedBudget[i] === 0 ? 'No Budget' : label)
  }

  let maxValue = 100
  let minValue = 0
  let dataSeries = []

  dataSeries.push({
    name: 'POs/Invoices',
    value: posP,
    ignoreLabel: true,
    color: isOverbudget ? chartColors.red : chartColors.blue,
    width: '15px'
  })

  if (hasExpenses) {
    dataSeries.push({
      name: 'Expenses',
      value: expP,
      ignoreLabel: true,
      color: chartColors.purple,
      width: '15px'
    })
  }

  let budgetChart = {
    type: 'Bar',
    stackBars: false,
    ignoreForCharts: false,
    data: [
      {
        name: 'Remaining Budget',
        value: fullBudget,
        ignoreLabel: true,
        color: chartColors.greyLight,
        width: '15px',
        labelData: labelData
      }
    ]
  }

  let consumptionChart = {
    type: 'Bar',
    stackBars: true,
    ignoreForCharts: false,
    data: dataSeries
  }

  let tooltipMetadata = getTooltipMetaData(hasExpenses, hasPerAttendeeCols, config.POsOrInvoicesLabel)
  let tooltipCData = {
    helpText: null,
    metadata: tooltipMetadata,
    data: [
      sortedData.map(k => k[ProgramName]),
      sortedData.map(k => k[RevisedBudget].toFixed(2)),
      sortedData.map(k => (k[ExcludeVATFromBudget] ? k[POTotalLessVatGst] : k[TotalProgramPOAmount]).toFixed(2)),
      sortedData.map(k => k[BudgetBalance].toFixed(2))
    ]
  }
  if (hasPerAttendeeCols) {
    tooltipCData.data.push(sortedData.map(k => (k[BudgetPerAttendee] === 0 ? null : k[BudgetPerAttendee])))
    tooltipCData.data.push(sortedData.map(k => (k[CostPerAttendee] === 0 ? null : k[CostPerAttendee])))
  }
  if (hasExpenses) {
    tooltipCData.data.push(sortedData.map(k => (k[IncludeExpenseInBudget] ? (k[ExcludeVATFromBudget] ? k[ExpenseTotalLessVatGst] : k[TotalProgramExpenseAmount]) : 0).toFixed(2)))
  }

  return {
    labels: sortedData.map(k => k[ProgramName]),
    series: [budgetChart, consumptionChart],
    tooltipData: tooltipCData,
    maxValue: maxValue,
    minValue: minValue
  }
}

function getTooltipMetaData(hasExpenses, hasPerAttendeeCols, posOrInvoicesLabel) {
  let tooltipMetadata = [
    {
      label: '',
      type: 'title',
      format: ''
    },
    {
      label: 'Revised Budget',
      type: 'main',
      format: 'currency'
    },
    {
      label: posOrInvoicesLabel || 'POs/Invoices',
      type: 'main',
      format: 'currency'
    },
    {
      label: 'Remaining Budget',
      type: 'secondary',
      format: 'currency'
    }
  ]
  if (hasPerAttendeeCols) {
    tooltipMetadata.push({ label: 'Budget Per Attendee', type: 'secondary', format: 'currency' })
    tooltipMetadata.push({ label: 'Cost Per Attendee', type: 'secondary', format: 'currency' })
  }
  if (hasExpenses) tooltipMetadata.push({ label: 'Expenses', type: 'main', format: 'currency' })
  return tooltipMetadata
}



// WEBPACK FOOTER //
// ./src/components/_modules/Dashboard/widgets/ProgramBudget/chart.js